import { FeatureToggles } from '@/features/feature-toggles'
import { type Member } from '@/features/iam/members/Member'
import { type TermsOfUseStore } from '@/features/iam/terms/TermsOfUseStore'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { action, computed, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import type { Id } from '@/infra/id'
import type { AlertErrorHandlerFactory } from '@/features/shell'

export class TermsModalViewModel {
  readonly modal = new ModalViewModel()

  @observable
  accessor termsContent = ''

  @observable accessor currentWorkspaceId: Id | null = null

  constructor(
    private readonly _termsStore: TermsOfUseStore,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  @computed
  get isNewTermsEnabled() {
    return FeatureToggles.newTerms()
  }

  /**
   * Shows the terms of use when the member's workspace requires them on login.
   *
   * @param member
   */
  async checkTermsForMember(member: Member) {
    const tos = member.workspace.config.termsOfService
    if (tos.hideOnWortal || !tos.showOnLogin) {
      return
    }

    // A failed check is not surfaced; it runs again on the next member change.
    await this.ensureTermsAccepted(
      tos.customTermsEnabled ? member.workspace.id : null,
    ).catch(() => undefined)
  }

  @action.bound
  async ensureTermsAccepted(workspaceId: Id | null) {
    if (!this.isNewTermsEnabled) {
      return
    }

    this.currentWorkspaceId = workspaceId
    const terms =
      await this._termsStore.fetchLatestTermsOfUseDocument(workspaceId)
    runInAction(() => {
      this.termsContent = terms.markup
    })

    const accepted =
      await this._termsStore.fetchTermsOfUseAcceptance(workspaceId)

    if (!accepted) {
      this.modal.show()
    }
  }

  /**
   * Accepts the latest terms. On failure the modal stays open so the member
   * can accept again.
   */
  accept = task.resolved(async () => {
    await this._termsStore
      .acceptLatestTerms(this.currentWorkspaceId)
      .catch(this._createAlertErrorHandler('Accepting terms failed'))
    this.modal.close()
  })
}
